import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';

import { AppBarWithButtons } from '../../../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppSelectExercise from '../../../../../components/designe/textfields/app-select-exercise/AppSelectExercise';
import AppDialog from '../../../../../components/reusable/app-dialog/AppDialog';
import { IPublicCompactExercise } from '../../../../../redux/api/exercises/interfaces';
import { IExercise } from '../../../../../types/interfaces/exercise';

interface IReplaceExerciseDialogProps {
  open: boolean;
  onClose: () => void;
  currentExerciseTitle: string;
  replacementExerciseId: string;
  setReplacementExerciseId: (id: string) => void;
  replacementExerciseOptions: IPublicCompactExercise[];
  isReplacingExercise: boolean;
  onSubmit: () => void | Promise<void>;
}

function compactExercisesToSelectList(
  items: IPublicCompactExercise[],
): IExercise[] {
  return items.map((item) => item as unknown as IExercise);
}

export default function ReplaceExerciseDialog({
  open,
  onClose,
  currentExerciseTitle,
  replacementExerciseId,
  setReplacementExerciseId,
  replacementExerciseOptions,
  isReplacingExercise,
  onSubmit,
}: IReplaceExerciseDialogProps) {
  const { t } = useTranslation();
  const exercisesForSelect = useMemo(
    () => compactExercisesToSelectList(replacementExerciseOptions),
    [replacementExerciseOptions],
  );
  const selectedReplacementExercise = useMemo(() => {
    const found = replacementExerciseOptions.find(
      (exerciseItem) => exerciseItem.id === replacementExerciseId,
    );
    return found ? (found as unknown as IExercise) : undefined;
  }, [replacementExerciseId, replacementExerciseOptions]);
  const hasValidReplacementSelection = replacementExerciseOptions.some(
    (exerciseItem) => exerciseItem.id === replacementExerciseId,
  );
  const isSubmitDisabled =
    !replacementExerciseId ||
    !hasValidReplacementSelection ||
    isReplacingExercise;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={t('exercises.replaceExerciseTitle')}>
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography lineHeight={1.5} variant="text-md">
          {t('exercises.replaceExerciseDescription', {
            exerciseTitle: currentExerciseTitle,
          })}
        </Typography>

        <Box marginY={2}>
          <AppSelectExercise
            label={t('exercises.replaceExerciseSelectLabel')}
            exercises={exercisesForSelect}
            value={selectedReplacementExercise}
            onChange={(exercise) => {
              setReplacementExerciseId(exercise?.id ?? '');
            }}
            disabled={isReplacingExercise}
          />
        </Box>
        <AppBarWithButtons
          onCancelClick={onClose}
          onClick={() => {
            if (isSubmitDisabled) {
              return;
            }

            void onSubmit();
          }}
          cancelDisabled={isReplacingExercise}
          submitLabel={t('exercises.replace')}
          disabled={isSubmitDisabled}
          loading={isReplacingExercise}
        />
      </Box>
    </AppDialog>
  );
}
